import { convertCountriesToCountryCalculationListItems, formatComparisonSubjectToInsitution } from '@modules';
import { FsType, FsTypeBase, InstitutionWithImage } from '@types';
import React, { useState } from 'react';
import { ClosableColorPicker, useColorAtIndex } from 'src/components/composite/colorpicker';
import ComparisonDropDownWithState from 'src/components/composite/finnoscore-comparison/ComparisonDropDownWithState';
import { ComparisonHeader } from 'src/components/composite/finnoscore-comparison/ComparisonSelector';
import { InstitutionDropDownWithState } from 'src/components/composite/finnoscore-comparison/InstitutionGroupedDropDown';
import { PacmanLoader } from 'src/components/composite/finnoscore-data/PacmanLoader';
import { Box } from 'theme-ui';
import { LegacySwitch } from '../../comparison/components/LegacySwitch';
import { ChartItem, fillColorStyles, PositionBar } from '../components/base';
import { DefaultChartData } from '../helpers';
import { chartStyles } from '../styles/chart.styles';
import { useCountryCalculationDropDown } from './useCountryCalculationDropDown';
import { useInstitutionDropDownControl } from './useInstitutionDropDownControl';

interface useOptionalCountryOrInstitutionDropdownProps {
  fsType?: FsTypeBase<FsType>;
  period?: FsTypeBase<string>;
  currentInstitution?: InstitutionWithImage;
  paramKey: string;
}

export function useOptionalCountryOrInstitutionDropdown({
  fsType,
  period,
  currentInstitution,
  paramKey,
}: useOptionalCountryOrInstitutionDropdownProps) {
  const [comparisonType, setComparisonType] = useState<number>(0);
  const [colorA, setColorA] = useColorAtIndex(0);

  const {
    institutions: relativeInstitutions,
    institution: relativeInstitution,
    institutionSetHandler: relativeInstitutionSetHandler,
  } = useInstitutionDropDownControl({ type: fsType, period, paramKey });

  const { countries, selectedCountry, selectedCountryHandler } = useCountryCalculationDropDown(
    fsType,
    period?.date,
    currentInstitution?.country?.countryCode,
    comparisonType
  );

  const getRelativeInstitution = () => {
    if (currentInstitution && relativeInstitution) {
      if (comparisonType === 0) return relativeInstitution;
      if (selectedCountry && comparisonType === 1) return formatComparisonSubjectToInsitution(selectedCountry);
    }
    return DefaultChartData.sample;
  };

  React.useEffect(() => {
    setComparisonType(0);
  }, [fsType, period?.id]);

  return {
    getCountryOrInstitution: getRelativeInstitution,
    relativeInstitution,
    comparisonType,
    setComparisonType,
    CountryOrInstitutionDropDown: () => (
      <React.Fragment>
        <LegacySwitch value={comparisonType} changeHandler={setComparisonType} />

        {comparisonType === 1 && !selectedCountry && <PacmanLoader sx={{ height: 'bigMobileIcon' }} />}
        {comparisonType === 1 && selectedCountry && countries && (
          <ComparisonDropDownWithState
            countries={countries && convertCountriesToCountryCalculationListItems(countries, fsType?.text)}
            setSelected={selectedCountryHandler}
            selectedCalculation={selectedCountry}
            header={
              <ComparisonHeader
                subject={selectedCountry}
                chartComponent={
                  <ChartItem
                    percentage={(selectedCountry?.total || 0) * 10}
                    sx={chartStyles.headerChart}
                    fillComponent={<PositionBar sx={fillColorStyles(colorA)} />}
                  />
                }
                colorPickerComponent={
                  <Box onClick={(e) => e.stopPropagation()}>
                    <ClosableColorPicker defaultColor={colorA} setColorHandler={setColorA} />
                  </Box>
                }
              />
            }
          />
        )}

        {comparisonType === 0 && !relativeInstitutions && <PacmanLoader sx={{ height: 'bigMobileIcon' }} />}
        {comparisonType === 0 && relativeInstitutions && (
          <InstitutionDropDownWithState
            institutions={relativeInstitutions}
            onSelect={relativeInstitutionSetHandler}
            selectedInstitution={relativeInstitution}
            sx={{ zIndex: 5 }}
            header={
              <ComparisonHeader
                subject={relativeInstitution}
                chartComponent={
                  <ChartItem
                    percentage={(relativeInstitution?.total || 0) * 10}
                    sx={chartStyles.headerChart}
                    fillComponent={<PositionBar sx={fillColorStyles(colorA)} />}
                  />
                }
                colorPickerComponent={
                  <Box onClick={(e) => e.stopPropagation()}>
                    <ClosableColorPicker defaultColor={colorA} setColorHandler={setColorA} />
                  </Box>
                }
              />
            }
          />
        )}
      </React.Fragment>
    ),
  };
}
